import React, { useState, useEffect } from "react";
import axios from "axios";
import { Star, Flame, Zap, Trophy, X } from "lucide-react";
import { API, useApp } from "../context";

const WelcomeBackSequence = ({ onComplete }) => {
  const { user, sounds } = useApp();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState(0); // 0=loading, 1=greeting, 2=stats, 3=tip, 4=done
  const [dismissed, setDismissed] = useState(false);
  const uid = user?.id || user?.email;

  // Temporarily disabled per product decision: kids should land straight on the
  // homepage with no returning-tour on every login. Flip to true to bring it back.
  const TOUR_ENABLED = false;

  useEffect(() => {
    if (!TOUR_ENABLED) { onComplete(); return; }
    if (!uid) { onComplete(); return; }
    // Check if already shown today
    const today = new Date().toISOString().slice(0, 10);
    const lastShown = sessionStorage.getItem("welcome_shown_date");
    if (lastShown === today) { onComplete(); return; }
    axios.get(`${API}/welcome-back/${uid}`).then(r => {
      setData(r.data);
      sessionStorage.setItem("welcome_shown_date", today);
      setPhase(1);
      if (sounds) sounds.playSound("notification");
    }).catch(() => onComplete());
  }, [uid]);

  useEffect(() => {
    if (phase === 0 || dismissed) return;
    const timers = [
      phase === 1 && setTimeout(() => setPhase(2), 2000),
      phase === 2 && setTimeout(() => setPhase(3), 3000),
      phase === 3 && setTimeout(() => { setPhase(4); onComplete(); }, 3000),
    ];
    return () => timers.forEach(t => t && clearTimeout(t));
  }, [phase, dismissed, onComplete]);

  const dismiss = () => {
    setDismissed(true);
    onComplete();
  };

  if (!data || phase === 0 || phase === 4 || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="welcome-back-overlay">
      {/* Backdrop with animated particles */}
      <div className="absolute inset-0 bg-black/70" onClick={dismiss} />

      {/* Central card */}
      <div className="relative max-w-sm w-full mx-4 rounded-3xl overflow-hidden" style={{
        animation: "welcome-entrance 0.6s ease-out",
      }}>
        {/* Animated gradient border */}
        <div className="absolute inset-0 rounded-3xl p-[2px]" style={{
          background: "linear-gradient(135deg, #FBBF24, #EC4899, #8B5CF6, #3B82F6, #FBBF24)",
          backgroundSize: "300% 300%",
          animation: "gradient-spin 4s linear infinite",
        }}>
          <div className="w-full h-full rounded-3xl bg-slate-900" />
        </div>

        <div className="relative p-6">
          {/* Close button */}
          <button onClick={dismiss} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white/70 transition-all" data-testid="dismiss-welcome">
            <X size={14} />
          </button>

          {/* Phase 1: Greeting */}
          {phase >= 1 && (
            <div className="text-center mb-4" style={{ animation: phase === 1 ? "welcome-fade-in 0.5s ease-out" : "none" }}>
              <div className="text-4xl mb-2" style={{ animation: "island-float 3s ease-in-out infinite" }}>
                {data.streak >= 7 ? "🔥" : data.streak >= 3 ? "⭐" : "👋"}
              </div>
              <h2 className="text-xl font-fredoka font-bold text-white">
                Welcome Back, Explorer!
              </h2>
              <p className="text-white/40 text-xs mt-1">
                {data.active_theme?.name} theme is active today
              </p>
            </div>
          )}

          {/* Phase 2: Stats flyover */}
          {phase >= 2 && (
            <div className="grid grid-cols-2 gap-2 mb-4" style={{ animation: "welcome-stats-in 0.5s ease-out" }}>
              <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/15 p-3 text-center">
                <Star size={14} className="mx-auto text-yellow-400 mb-1" />
                <p className="text-yellow-300 text-lg font-bold">{data.stars}</p>
                <p className="text-white/30 text-[9px]">Stars</p>
              </div>
              <div className="rounded-xl bg-orange-500/10 border border-orange-500/15 p-3 text-center">
                <Flame size={14} className="mx-auto text-orange-400 mb-1" />
                <p className="text-orange-300 text-lg font-bold">{data.streak}d</p>
                <p className="text-white/30 text-[9px]">Streak</p>
              </div>
              <div className="rounded-xl bg-blue-500/10 border border-blue-500/15 p-3 text-center">
                <Zap size={14} className="mx-auto text-blue-400 mb-1" />
                <p className="text-blue-300 text-lg font-bold">{data.today_catches}</p>
                <p className="text-white/30 text-[9px]">Today's Catches</p>
              </div>
              <div className="rounded-xl bg-green-500/10 border border-green-500/15 p-3 text-center">
                <Trophy size={14} className="mx-auto text-green-400 mb-1" />
                <p className="text-green-300 text-lg font-bold">{data.worlds_visited}</p>
                <p className="text-white/30 text-[9px]">Worlds Visited</p>
              </div>
            </div>
          )}

          {/* Phase 3: Tip */}
          {phase >= 3 && (
            <div className="rounded-xl bg-cyan-500/10 border border-cyan-500/15 p-3 flex gap-2 mb-3" style={{ animation: "welcome-fade-in 0.5s ease-out" }}>
              <span className="text-lg">🦉</span>
              <p className="text-white/70 text-xs leading-relaxed">{data.tip}</p>
            </div>
          )}

          {/* Skip / Continue */}
          <button onClick={dismiss} className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-xs font-bold hover:bg-white/10 transition-all" data-testid="continue-btn">
            {phase < 3 ? "Skip" : "Let's Go!"}
          </button>
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className="absolute rounded-full" style={{
            width: 4 + Math.random() * 6, height: 4 + Math.random() * 6,
            top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`,
            background: ['#FBBF24', '#A78BFA', '#34D399', '#F472B6', '#38BDF8'][i % 5],
            opacity: 0.3,
            animation: `star-pulse ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 3}s infinite`,
          }} />
        ))}
      </div>
    </div>
  );
};

export default WelcomeBackSequence;
